import { sortBy, uniq } from "lodash";
import { createSelector } from "reselect";

import { EContractStatus } from "constants/eContract";
import {
  FieldToGroupLabelDescriptionMap,
  SaleSearchFields,
} from "constants/globalSearch";
import { SaleStatusLabels } from "constants/sale";

import { resolveSaleStatus } from "lib/saleStatus";

import { selectEffectiveSaleTitleBySaleIdLookup } from "../sales";
import { getActiveRole, getSales, getSpecies, getSaleStatuses } from "../root";
import { selectSaleRoleNameByUserId } from "../saleRoles";

const E_CONTRACT_STATUS_ORDER = Object.values(EContractStatus);

const createSaleOption = (labelField, valueField, value) => ({
  label: labelField,
  value: {
    field: valueField,
    value,
  },
});

const createSaleSearchSelector = (valueField, mapFunction) =>
  createSelector([getSales], sales => {
    return sortBy(
      uniq(Object.values(sales).map(mapFunction)).map(option =>
        createSaleOption(option, valueField, option),
      ),
      "label",
    );
  });

const selectSaleSearchSaleIds = createSaleSearchSelector(
  SaleSearchFields.SaleId,
  sale => sale.livestocksale_id,
);

const selectSaleSearchSaleDate = createSaleSearchSelector(
  SaleSearchFields.Date,
  sale => sale.date,
);

const selectSaleSearchSaleType = createSaleSearchSelector(
  SaleSearchFields.SaleType,
  sale => sale.sale_type,
);

const selectSaleSearchSaleSaleyard = createSaleSearchSelector(
  SaleSearchFields.Saleyard,
  sale => sale.saleyard_name,
);

const selectSaleSearchSaleEContractStatus = createSelector(
  [getSales],
  sales => {
    const statuses = uniq(
      Object.values(sales)
        .map(sale => sale.eContractStatus)
        .filter(Boolean),
    );
    return sortBy(
      statuses.map(status =>
        createSaleOption(status, SaleSearchFields.EContractStatus, status),
      ),
      option => E_CONTRACT_STATUS_ORDER.indexOf(option.value.value),
    );
  },
);

const selectSaleSearchSaleTitle = createSelector(
  [getSales, selectEffectiveSaleTitleBySaleIdLookup],
  (sales, effectiveTitleBySaleId) => {
    const titles = uniq(
      Object.values(sales).map(
        sale => effectiveTitleBySaleId[sale.livestocksale_id],
      ),
    ).filter(Boolean);
    return sortBy(
      titles.map(title =>
        createSaleOption(title, SaleSearchFields.Title, title),
      ),
      "label",
    );
  },
);

const selectSaleSearchSaleSpecies = createSelector(
  [getSpecies, getSales],
  (species, sales) => {
    return sortBy(
      uniq(Object.values(sales).map(sale => sale.species_id)).map(
        saleSpecies => {
          const speciesName = species[saleSpecies]?.name || "";
          return createSaleOption(
            speciesName,
            SaleSearchFields.Species,
            saleSpecies,
          );
        },
      ),
      "label",
    );
  },
);

const selectSaleSearchSaleStatus = createSelector(
  [getSales, getSaleStatuses, getActiveRole],
  (sales, saleStatusesById, role) => {
    // Grab the custom statuses - we'll only see these
    // if we have them in our catalogue of statuses (literally - am I an agent).

    const customStatusOptions = new Set();
    const baseStatusOptions = new Set();

    Object.values(sales).forEach(sale => {
      const { archetype, customStatus } = resolveSaleStatus(
        sale,
        role,
        saleStatusesById,
      );
      if (customStatus) {
        customStatusOptions.add(customStatus.id);
      } else {
        baseStatusOptions.add(archetype);
      }
    });
    const customOptionsWithLabel = [...customStatusOptions].map(saleStatusId =>
      createSaleOption(
        saleStatusesById[saleStatusId].name,
        SaleSearchFields.Status,
        saleStatusId,
      ),
    );
    const optionsWithLabel = [...baseStatusOptions].map(option =>
      createSaleOption(
        SaleStatusLabels[option],
        SaleSearchFields.Status,
        option,
      ),
    );

    return sortBy(customOptionsWithLabel.concat(optionsWithLabel), "label");
  },
);

// Distinct managers / assignees across the loaded sales, as {label: name,
// value: {field, value: userId}} options for the search dropdown. Names are
// resolved from the agent directory, like the dashboard chips.
const createSaleRoleSearchSelector = (idField, field) =>
  createSelector(
    [getSales, selectSaleRoleNameByUserId],
    (sales, nameByUserId) => {
      const userIds = new Set();
      Object.values(sales).forEach(sale => {
        (sale.sale_attributes?.[idField] || []).forEach(id => userIds.add(id));
      });
      return sortBy(
        [...userIds]
          .filter(id => nameByUserId[id])
          .map(id => createSaleOption(nameByUserId[id], field, id)),
        "label",
      );
    },
  );

const selectSaleSearchManagers = createSaleRoleSearchSelector(
  "managerIds",
  SaleSearchFields.Managers,
);

const selectSaleSearchAssignees = createSaleRoleSearchSelector(
  "assigneeIds",
  SaleSearchFields.Assignees,
);

export const selectSaleSearchOptions = createSelector(
  [
    selectSaleSearchSaleDate,
    selectSaleSearchSaleIds,
    selectSaleSearchSaleSaleyard,
    selectSaleSearchSaleSpecies,
    selectSaleSearchSaleStatus,
    selectSaleSearchSaleTitle,
    selectSaleSearchSaleType,
    selectSaleSearchManagers,
    selectSaleSearchAssignees,
    selectSaleSearchSaleEContractStatus,
  ],
  (
    saleDateOptions,
    saleIdOptions,
    saleSaleyardOptions,
    saleSpeciesOptions,
    saleStatusOptions,
    salesTitleOptions,
    saleTypeOptions,
    saleManagerOptions,
    saleAssigneeOptions,
    saleEContractStatusOptions,
  ) => {
    return [
      {
        label: FieldToGroupLabelDescriptionMap[SaleSearchFields.Date],
        field: SaleSearchFields.Date,
        options: saleDateOptions,
      },
      {
        label: FieldToGroupLabelDescriptionMap[SaleSearchFields.SaleId],
        field: SaleSearchFields.SaleId,
        options: saleIdOptions,
      },
      {
        label: FieldToGroupLabelDescriptionMap[SaleSearchFields.Saleyard],
        field: SaleSearchFields.Saleyard,
        options: saleSaleyardOptions,
      },
      {
        label: FieldToGroupLabelDescriptionMap[SaleSearchFields.Species],
        field: SaleSearchFields.Species,
        options: saleSpeciesOptions,
      },
      {
        label: FieldToGroupLabelDescriptionMap[SaleSearchFields.Status],
        field: SaleSearchFields.Status,
        options: saleStatusOptions,
      },
      {
        label: FieldToGroupLabelDescriptionMap[SaleSearchFields.Title],
        field: SaleSearchFields.Title,
        options: salesTitleOptions,
      },
      {
        label: FieldToGroupLabelDescriptionMap[SaleSearchFields.SaleType],
        field: SaleSearchFields.SaleType,
        options: saleTypeOptions,
      },
      {
        label: FieldToGroupLabelDescriptionMap[SaleSearchFields.Managers],
        field: SaleSearchFields.Managers,
        options: saleManagerOptions,
      },
      {
        label: FieldToGroupLabelDescriptionMap[SaleSearchFields.Assignees],
        field: SaleSearchFields.Assignees,
        options: saleAssigneeOptions,
      },
      {
        label:
          FieldToGroupLabelDescriptionMap[SaleSearchFields.EContractStatus],
        field: SaleSearchFields.EContractStatus,
        options: saleEContractStatusOptions,
      },
    ];
  },
);
